import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';
import SpeedOutlinedIcon from '@mui/icons-material/SpeedOutlined';
import EqualizerOutlinedIcon from '@mui/icons-material/EqualizerOutlined';
import SentimentSatisfiedAltOutlinedIcon from '@mui/icons-material/SentimentSatisfiedAltOutlined';
import SentimentNeutralOutlinedIcon from '@mui/icons-material/SentimentNeutralOutlined';
import SentimentDissatisfiedOutlinedIcon from '@mui/icons-material/SentimentDissatisfiedOutlined';
import FiberNewOutlinedIcon from '@mui/icons-material/FiberNewOutlined';

/**
 * SummaryCard - Card individual de KPI (mesmo visual de CompanySummaryCards)
 */
function SummaryCard({ title, value, icon, iconColor, iconBg }) {
  return (
    <Card
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        p: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        transition: 'box-shadow 0.2s ease',
        '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)' },
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, mb: 0.5, fontSize: '0.8125rem' }}>
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, fontSize: '1.75rem', color: 'text.primary', lineHeight: 1 }}>
            {value}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            bgcolor: iconBg,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, { sx: { color: iconColor, fontSize: 20 } })}
        </Box>
      </Box>
    </Card>
  );
}

/**
 * NpsSummaryCards - Cards de KPI da aba "Avaliações (NPS)" (Admin Geral)
 * @param {Object} props
 * @param {{ total: number, score: number, average: number|null, promoters: number, passives: number, detractors: number, new: number }} props.summary
 */
function NpsSummaryCards({ summary }) {
  const s = summary || { total: 0, score: 0, average: null, promoters: 0, passives: 0, detractors: 0, new: 0 };

  const cards = [
    { title: 'Total', value: s.total, icon: <StarOutlineOutlinedIcon />, iconColor: '#5A80FB', iconBg: 'rgba(90, 128, 251, 0.1)' },
    { title: 'NPS Score', value: s.score, icon: <SpeedOutlinedIcon />, iconColor: '#4527A0', iconBg: '#EDE7F6' },
    { title: 'Média', value: s.average ?? '—', icon: <EqualizerOutlinedIcon />, iconColor: '#00838F', iconBg: '#E0F7FA' },
    { title: 'Promotores', value: s.promoters, icon: <SentimentSatisfiedAltOutlinedIcon />, iconColor: '#2E7D32', iconBg: '#E8F5E9' },
    { title: 'Neutros', value: s.passives, icon: <SentimentNeutralOutlinedIcon />, iconColor: '#E65100', iconBg: '#FFF3E0' },
    { title: 'Detratores', value: s.detractors, icon: <SentimentDissatisfiedOutlinedIcon />, iconColor: '#C62828', iconBg: '#FFEBEE' },
    { title: 'Novos', value: s.new, icon: <FiberNewOutlinedIcon />, iconColor: '#616161', iconBg: '#F5F5F5' },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(7, 1fr)' },
        gap: 2,
        mb: 3,
      }}
    >
      {cards.map((card, index) => (
        <SummaryCard key={index} {...card} />
      ))}
    </Box>
  );
}

export default NpsSummaryCards;
